import React, {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import styles from './Navbar.module.css';
import siteConfig from '../../config/siteConfig';
import {useTranslation} from '../../hooks/useTranslation';
import LanguageToggle from '../ui/LanguageToggle';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const t = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, {passive: true});
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const handleNavClick = (e, href) => {
    if (!isLandingPage || !href.startsWith('#')) return;
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({behavior: 'smooth', block: 'start'});
      setMenuOpen(false);
    }
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`} role="banner">
      {/* Skip to main content — accessibility */}
      <a href="#main" className={styles.skipLink}>
        Skip to main content
      </a>
      <nav className={`${styles.nav} container`} aria-label="Main navigation">
        <Link to="/" className={styles.logo} aria-label="Your Change Mentor — Home">
          {/*
            Logo PNG: place file at public/images/logo-wordmark.png
            (Logo 2 — dragonfly + "Your Change Mentor" without subtitle)
            SVG dragonfly + text serves as fallback when PNG is absent.
          */}
          <img
            src={`${process.env.PUBLIC_URL}/images/logo-badge.png`}
            alt="Your Change Mentor"
            className={styles.logoImg}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.parentElement.querySelector(`.${styles.logoFallback}`);
              if (fb) fb.style.display = 'flex';
            }}
          />
          <span className={styles.logoFallback}>
            <svg
              className={styles.logoMark}
              viewBox="0 0 40 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <ellipse cx="10" cy="18" rx="10" ry="4.5" fill="#98dbd8" opacity="0.92" transform="rotate(-18 10 18)" />
              <ellipse cx="30" cy="18" rx="10" ry="4.5" fill="#98dbd8" opacity="0.92" transform="rotate(18 30 18)" />
              <ellipse cx="11" cy="26" rx="7.5" ry="3" fill="#bb9cd5" opacity="0.80" transform="rotate(12 11 26)" />
              <ellipse cx="29" cy="26" rx="7.5" ry="3" fill="#bb9cd5" opacity="0.80" transform="rotate(-12 29 26)" />
              <ellipse cx="20" cy="28" rx="2.2" ry="10" fill="#d8e305" />
              <circle cx="20" cy="16" r="3.5" fill="#f1ae31" />
              <circle cx="18.4" cy="15" r="1" fill="#2C3E50" />
              <circle cx="21.6" cy="15" r="1" fill="#2C3E50" />
            </svg>
            <span className={styles.logoName}>{siteConfig.site.name}</span>
          </span>
        </Link>

        {/* Desktop nav */}
        {isLandingPage && (
          <ul className={styles.links}>
            {t.nav.links.map(({label, href}) => (
              <li key={label}>
                <a
                  href={href}
                  className={styles.link}
                  onClick={(e) => handleNavClick(e, href)}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.actions}>
          <LanguageToggle />
          <a
            href={isLandingPage ? '#lead-capture' : '/'}
            className={`btn btn-primary ${styles.cta}`}
            onClick={(e) => handleNavClick(e, '#lead-capture')}
          >
            {t.nav.cta}
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className={styles.hamburger}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className={`${styles.bar} ${menuOpen ? styles.open : ''}`} />
          <span className={`${styles.bar} ${menuOpen ? styles.open : ''}`} />
          <span className={`${styles.bar} ${menuOpen ? styles.open : ''}`} />
        </button>
      </nav>

      {/* Mobile menu */}
      {isLandingPage && (
        <>
          {menuOpen && (
            <div
              className={styles.backdrop}
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
          )}
          <div
            id="mobile-menu"
            className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}
            aria-hidden={!menuOpen}
          >
            <ul>
              {t.nav.links.map(({label, href}) => (
                <li key={label}>
                  <a
                    href={href}
                    className={styles.mobileLink}
                    onClick={(e) => handleNavClick(e, href)}
                    tabIndex={menuOpen ? 0 : -1}
                  >
                    {label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#lead-capture"
                  className={`btn btn-primary ${styles.mobileCta}`}
                  onClick={(e) => handleNavClick(e, '#lead-capture')}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  {t.nav.cta}
                </a>
              </li>
              <li className={styles.mobileLangToggle}>
                <LanguageToggle />
              </li>
            </ul>
          </div>
        </>
      )}
    </header>
  );
}
